/* FREDDY Pantroom — predictive search inside the search dialog (00-architecture §E.5).
   Fetches the API-only section freddy-predictive-search through the Predictive Search API and
   moves its .freddy-predictive__results into #predictive-search. The server markup of the dialog
   stays concept-identical; ARIA attributes are added at runtime only. */
(() => {
  'use strict';

  const F = (window.FREDDY = window.FREDDY || {});
  const settings = F.settings || {};
  if (settings.predictiveSearch === false) return;

  const strings = () => F.strings || {};
  const routes = F.routes || {};
  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const ENDPOINT = routes.predictiveSearch || shopRoot + 'search/suggest';
  const SECTION_ID = 'freddy-predictive-search';
  const MIN_LENGTH = 2;
  const DEBOUNCE_MS = 250;
  const LOADING_DELAY_MS = 300;

  let controller = null;
  let debounceTimer = 0;
  let loadingTimer = 0;
  let shownTerms = '';

  const getPanel = () => document.getElementById('predictive-search');
  const getInput = () => document.getElementById('search-input');
  const getSuggestions = () => document.querySelector('#search-dialog .search-suggestions');
  const isSearchInput = (el) => el instanceof HTMLInputElement && el.id === 'search-input';

  function setup() {
    const panel = getPanel();
    const input = getInput();
    if (!panel || !input) return;
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', 'predictive-search');
    input.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
  }

  function abort() {
    clearTimeout(loadingTimer);
    if (controller) {
      controller.abort();
      controller = null;
    }
  }

  function hide() {
    abort();
    shownTerms = '';
    const panel = getPanel();
    if (panel) {
      panel.hidden = true;
      panel.replaceChildren();
    }
    const suggestions = getSuggestions();
    if (suggestions) suggestions.hidden = false;
    const input = getInput();
    if (input && input.hasAttribute('aria-expanded')) input.setAttribute('aria-expanded', 'false');
  }

  /* Liquid `t` output is HTML-escaped on Shopify: decode before using it as text. */
  function decode(value) {
    const raw = String(value == null ? '' : value);
    if (raw.indexOf('&') === -1) return raw;
    const area = document.createElement('textarea');
    area.innerHTML = raw;
    return area.value;
  }

  function showLoading(panel) {
    const p = document.createElement('p');
    p.className = 'freddy-predictive__loading';
    p.textContent = decode(strings().searchLoading) || '…';
    panel.replaceChildren(p);
    panel.hidden = false;
  }

  function buildUrl(terms) {
    const limit = Math.min(10, Math.max(1, parseInt(settings.predictiveLimit, 10) || 4));
    return ENDPOINT + '?q=' + encodeURIComponent(terms) +
      '&resources[type]=product,collection,page,article,query' +
      '&resources[limit]=' + limit +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&section_id=' + SECTION_ID;
  }

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function search(terms) {
    const panel = getPanel();
    if (!panel) return;
    abort();
    const request = new AbortController();
    controller = request;
    const { signal } = request;
    loadingTimer = setTimeout(() => {
      if (!signal.aborted) showLoading(panel);
    }, LOADING_DELAY_MS);

    try {
      const url = buildUrl(terms);
      let response = await fetch(url, { signal, credentials: 'same-origin' });
      if (response.status === 429) {
        const retryAfter = parseFloat(response.headers.get('Retry-After'));
        await wait(Math.min(10, Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1) * 1000);
        if (signal.aborted) return;
        response = await fetch(url, { signal, credentials: 'same-origin' });
      }
      if (signal.aborted) return;
      clearTimeout(loadingTimer);
      if (!response.ok) {
        hide(); // 417 (unsupported language), 422, repeated 429 …: fail silently
        return;
      }
      const html = await response.text();
      if (signal.aborted) return;
      const results = new DOMParser().parseFromString(html, 'text/html').querySelector('.freddy-predictive__results');
      if (!results) {
        hide();
        return;
      }
      panel.replaceChildren(document.importNode(results, true));
      panel.hidden = false;
      const suggestions = getSuggestions();
      if (suggestions) suggestions.hidden = true;
      const input = getInput();
      if (input) input.setAttribute('aria-expanded', 'true');
      shownTerms = terms;
      if (controller === request) controller = null;
    } catch (error) {
      clearTimeout(loadingTimer);
      if (error && error.name === 'AbortError') return;
      hide();
    }
  }

  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!isSearchInput(input) || !getPanel()) return;
    clearTimeout(debounceTimer);
    const terms = input.value.trim();
    if (terms.length < MIN_LENGTH) {
      hide();
      return;
    }
    debounceTimer = setTimeout(() => {
      const panel = getPanel();
      if (terms === shownTerms && panel && !panel.hidden) return;
      search(terms);
    }, DEBOUNCE_MS);
  });

  function selectableLinks(panel) {
    return Array.from(panel.querySelectorAll('a[href]'));
  }

  function select(links, index) {
    links.forEach((link, i) => {
      if (i === index) {
        link.setAttribute('aria-selected', 'true');
        link.scrollIntoView({ block: 'nearest' });
      } else {
        link.removeAttribute('aria-selected');
      }
    });
  }

  document.addEventListener('keydown', (event) => {
    const input = event.target;
    if (!isSearchInput(input)) return;
    const panel = getPanel();
    if (!panel || panel.hidden) return;
    const links = selectableLinks(panel);
    if (!links.length) return;
    const index = links.findIndex((link) => link.getAttribute('aria-selected') === 'true');

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      select(links, index + 1 >= links.length ? 0 : index + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      select(links, index === -1 ? links.length - 1 : index - 1);
    } else if (event.key === 'Enter' && index >= 0) {
      event.preventDefault();
      window.location.assign(links[index].href);
    }
    // Plain Enter (nothing selected) submits the dialog form: /search?type=product&q=…
  });

  document.addEventListener('mouseover', (event) => {
    const panel = getPanel();
    if (!panel || panel.hidden || !(event.target instanceof Element)) return;
    const link = event.target.closest('#predictive-search a[href]');
    if (link) select(selectableLinks(panel), -1);
  });

  document.addEventListener('freddy:dialog:open', (event) => {
    if (!event.detail || event.detail.id !== 'search-dialog') return;
    setup();
    const input = getInput();
    const panel = getPanel();
    if (input && panel && panel.hidden && input.value.trim().length >= MIN_LENGTH) search(input.value.trim());
  });

  document.addEventListener('freddy:dialog:close', (event) => {
    if (!event.detail || event.detail.id !== 'search-dialog') return;
    clearTimeout(debounceTimer);
    abort();
    const panel = getPanel();
    if (panel) select(selectableLinks(panel), -1);
  });

  document.addEventListener('shopify:section:load', () => {
    shownTerms = '';
    setup();
  });

  F.search = Object.assign(F.search || {}, { predictive: search, hide });

  setup();
})();
